import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Image from 'next/image';
import { useLocale } from '@hooks/useLocale';
import { useTranslation } from 'next-i18next';

import Heading from '@components/ui/Heading';
import { MecProduct, PriceGroup } from '@adapters/bigcommerce/types/product';
import useProduct from '@adapters/bigcommerce/hooks/product/useProduct';
import ProductPrice from '@components/global/Comparison/ProductPrice/ProductPrice';
import RatingStars from '../ProductRatingStars';
import Skeleton from 'react-loading-skeleton';
import { transformProductPrice } from '@utils/comparison/transformProductPrice';
import { CompareSpec } from '@utils/comparison/buildProductCompareTableProps';

import styles from './ProductComparison.module.scss';

interface ProductComparisonProps {
  product: MecProduct;
  productCompareSpecs?: CompareSpec[];
  productCompareProductIds?: string[];
}

export const PRODUCT_NAME_EN = 'Name';
export const PRODUCT_NAME_FR = 'Nom';
export const PRODUCT_PRICE_EN = 'Price';
export const PRODUCT_PRICE_FR = 'Prix';
const PRODUCT_RATING_EN = 'Rating';
const PRODUCT_RATING_FR = 'Évaluations';

const ProductComparison = ({
  product,
  productCompareSpecs = [],
  productCompareProductIds = [],
}: ProductComparisonProps) => {
  const locale = useLocale();
  const { t } = useTranslation('comparison');
  const { getProduct } = useProduct();
  const [mecProducts, setMecProducts] = useState<MecProduct[]>([]);
  const comparisonTableRef = useRef<HTMLTableElement>(null);

  const productCodes = useMemo(
    () => [product.code, ...productCompareProductIds],
    [productCompareProductIds, product.code]
  );

  useEffect(() => {
    const fetchProducts = async () => {
      const fetchedProducts = await Promise.all(
        productCodes.map((code) => (code ? getProduct(code) : null))
      );
      setMecProducts(
        fetchedProducts.filter((p): p is MecProduct => p !== null)
      );
    };

    fetchProducts();
  }, [getProduct, productCodes]);

  const hasValidComparisonData = (mecProduct: MecProduct): boolean => {
    if (productCompareSpecs.length > 0) {
      return productCompareSpecs.some((key) => {
        if (
          key[locale as keyof typeof key] === PRODUCT_NAME_EN ||
          key[locale as keyof typeof key] === PRODUCT_NAME_FR
        ) {
          return !!mecProduct.name;
        }
        if (
          key[locale as keyof typeof key] === PRODUCT_PRICE_EN ||
          key[locale as keyof typeof key] === PRODUCT_PRICE_FR
        ) {
          return !!mecProduct.price;
        }
        if (
          key[locale as keyof typeof key] === PRODUCT_RATING_EN ||
          key[locale as keyof typeof key] === PRODUCT_RATING_FR
        ) {
          return (
            mecProduct.reviewSummary?.summationOfRatings > 0 ||
            mecProduct.reviewSummary?.numberOfReviews > 0
          );
        }

        const techSpec = mecProduct.techSpecs.find(
          (mecSpec) => mecSpec.key === key[locale as keyof typeof key]
        );
        return (
          techSpec?.value && techSpec.value !== '' && techSpec.value !== '0'
        );
      });
    }

    return false;
  };

  const getValidFieldTypes = (products: MecProduct[]) => {
    const specFieldTypes = productCompareSpecs.map((spec) => ({
      type: String(spec[locale as keyof typeof spec])
        .toLowerCase()
        .replace(/\s+/g, '') as any,
      hasData: false,
      specKey: String(spec[locale as keyof typeof spec]),
    }));

    products.forEach((mecProduct) => {
      specFieldTypes.forEach(({ type, specKey }) => {
        let hasData = false;

        if (specKey === PRODUCT_NAME_EN || specKey === PRODUCT_NAME_FR) {
          hasData = !!mecProduct.name;
        } else if (
          specKey === PRODUCT_PRICE_EN ||
          specKey === PRODUCT_PRICE_FR
        ) {
          hasData = !!mecProduct.price;
        } else if (
          specKey === PRODUCT_RATING_EN ||
          specKey === PRODUCT_RATING_FR
        ) {
          hasData = !!(
            mecProduct.reviewSummary?.summationOfRatings > 0 ||
            mecProduct.reviewSummary?.numberOfReviews > 0
          );
        } else {
          const techSpec = mecProduct.techSpecs.find(
            (mecSpec) => mecSpec.key === specKey
          );
          hasData = !!(
            techSpec?.value &&
            techSpec.value !== '' &&
            techSpec.value !== '0'
          );
        }

        if (hasData) {
          specFieldTypes.find((f) => f.type === type)!.hasData = true;
        }
      });
    });

    const validSpecFields = specFieldTypes
      .filter((field) => field.hasData)
      .map((field) => ({ type: field.type, specKey: field.specKey }));

    return { validSpecFields };
  };

  const productPrices = useMemo(() => {
    const prices = new Map<string, PriceGroup | null>();
    mecProducts.forEach((item) => {
      if (item?.code) {
        prices.set(item.code, transformProductPrice(item));
      }
    });
    return prices;
  }, [mecProducts]);

  const renderComparisonField = (mecProduct: MecProduct, specKey?: string) => {
    if (specKey) {
      if (specKey === PRODUCT_NAME_EN || specKey === PRODUCT_NAME_FR) {
        return mecProduct.name ? (
          <a
            href={`/product/${mecProduct.code}`}
            className={styles.comparisonItemFieldLink}
          >
            <Heading
              styleLevel="7"
              semanticLevel="h3"
              className={styles.comparisonItemFieldName}
            >
              {mecProduct.name}
            </Heading>
          </a>
        ) : null;
      }
      if (specKey === PRODUCT_PRICE_EN || specKey === PRODUCT_PRICE_FR) {
        const productPrice = productPrices.get(mecProduct?.code ?? '');
        return productPrice ? (
          <div className={styles.comparisonItemFieldPrice}>
            <ProductPrice price={productPrice} showOriginalPrice={false} />
          </div>
        ) : null;
      }
      if (specKey === PRODUCT_RATING_EN || specKey === PRODUCT_RATING_FR) {
        const rating = mecProduct.reviewSummary?.summationOfRatings;
        const reviews = mecProduct.reviewSummary?.numberOfReviews;

        return (
          <div>
            <RatingStars rating={rating} />
            <span className={styles.reviewTotal}>{`(${reviews})`}</span>
          </div>
        );
      }

      const techSpec = mecProduct.techSpecs.find(
        (spec) => spec.key === specKey
      );

      const value = techSpec?.value;
      const specValue = Array.isArray(value) ? value.join(', ') : value || '';

      if (!specValue || specValue === '0') {
        return <p>N/A</p>;
      }

      return <p dangerouslySetInnerHTML={{ __html: specValue }} />;
    }
    return null;
  };

  const validProducts = mecProducts.filter((mecProduct) =>
    hasValidComparisonData(mecProduct)
  );

  const { validSpecFields } = getValidFieldTypes(validProducts);

  const comparisonFields: Array<{
    type: string;
    label: string;
    specKey?: string;
  }> = [
    ...validSpecFields.map(({ type, specKey }) => ({
      type,
      label: specKey!,
      specKey,
    })),
  ];

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableElement>) => {
      const table = comparisonTableRef.current;
      if (!table) return;

      const focusableCells = Array.from(
        table.querySelectorAll<HTMLElement>(
          'td[tabindex="0"], th[tabindex="0"], a, button, [tabindex="0"]'
        )
      );
      const currentIndex = focusableCells.findIndex(
        (cell) => cell === document.activeElement
      );

      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = Math.min(currentIndex + 1, focusableCells.length - 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = Math.max(currentIndex - 1, 0);
          break;
        case 'ArrowDown': {
          e.preventDefault();
          const currentCell = focusableCells[currentIndex];
          const currentRow = currentCell.closest('tr');
          if (!currentRow) return;

          const allRows = Array.from(
            table.querySelectorAll('tbody tr, thead tr')
          );
          const currentRowIndex = allRows.indexOf(currentRow);
          const nextRow = allRows[currentRowIndex + 1];

          if (nextRow) {
            const cellIndex = Array.from(currentRow.children).indexOf(
              currentCell as HTMLElement
            );
            const nextCell = nextRow.children[cellIndex] as HTMLElement;
            if (nextCell) {
              const focusableInNextCell =
                nextCell.querySelector<HTMLElement>(
                  'a, button, [tabindex="0"]'
                ) || (nextCell.hasAttribute('tabindex') ? nextCell : null);
              if (focusableInNextCell) {
                focusableInNextCell.focus();
              }
            }
          }
          return;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const currentCell = focusableCells[currentIndex];
          const currentRow = currentCell.closest('tr');
          if (!currentRow) return;

          const allRows = Array.from(
            table.querySelectorAll('tbody tr, thead tr')
          );
          const currentRowIndex = allRows.indexOf(currentRow);
          const nextRow = allRows[currentRowIndex - 1];

          if (nextRow) {
            const cellIndex = Array.from(currentRow.children).indexOf(
              currentCell as HTMLElement
            );
            const nextCell = nextRow.children[cellIndex] as HTMLElement;
            if (nextCell) {
              const focusableInNextCell =
                nextCell.querySelector<HTMLElement>(
                  'a, button, [tabindex="0"]'
                ) || (nextCell.hasAttribute('tabindex') ? nextCell : null);
              if (focusableInNextCell) {
                focusableInNextCell.focus();
              }
            }
          }
          return;
        }
        case 'Home':
          e.preventDefault();
          nextIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          nextIndex = focusableCells.length - 1;
          break;
        default:
          return;
      }

      if (nextIndex !== currentIndex) {
        focusableCells[nextIndex]?.focus();
      }
    },
    []
  );

  const mockArray = [1, 2, 3, 4];
  // Show loading state when products are being fetched
  if (mecProducts.length === 0) {
    return (
      <div className={styles.comparisonContainer}>
        <table
          ref={comparisonTableRef}
          className={styles.comparisonTable}
          aria-label={t('title')}
          onKeyDown={handleKeyDown}
          role="grid"
        >
          <caption className={styles.srOnly}>{t('title')}</caption>
          <colgroup>
            <col className={styles.fieldLabelColumn} />
            {mockArray.map((_, index) => (
              <col key={`col-${index}`} className={styles.productColumn} />
            ))}
          </colgroup>
          <thead>
            <tr className={styles.headerRow}>
              <th scope="col" className={styles.fieldLabel}></th>
              {mockArray.map((mockProduct) => (
                <th
                  key={`product-${mockProduct}`}
                  scope="col"
                  className={styles.imageHeader}
                >
                  <div className={styles.image}>
                    <Skeleton height={150} width={150} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mockArray.map((mockField) => (
              <tr key={`field-${mockField}`}>
                <th scope="row" className={styles.fieldLabel}>
                  <Skeleton height={30} width={100} />
                </th>
                {mockArray.map((mockProduct) => (
                  <td
                    key={`productField-${mockProduct}`}
                    className={styles.comparisonItemField}
                    tabIndex={0}
                    role="gridcell"
                  >
                    <Skeleton height={30} width={200} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={styles.comparisonContainer}>
      {validProducts.length > 0 && (
        <table
          ref={comparisonTableRef}
          className={styles.comparisonTable}
          aria-label={t('title')}
        >
          <caption className={styles.srOnly}>{t('title')}</caption>
          <colgroup>
            <col className={styles.fieldLabelColumn} />
            {validProducts.map((_, index) => (
              <col key={`col-${index}`} className={styles.productColumn} />
            ))}
          </colgroup>
          <thead>
            <tr className={styles.headerRow}>
              <th scope="col" className={styles.fieldLabel}></th>
              {validProducts.map((validProduct, index) => (
                <th
                  key={validProduct.code}
                  scope="col"
                  className={`${styles.imageHeader} ${
                    index === 0 ? styles.alternateColumn : ''
                  }`}
                >
                  <div className={styles.image}>
                    {validProduct?.images?.[0]?.urlOriginal && (
                      <Image
                        src={validProduct?.images?.[0]?.urlOriginal}
                        alt={validProduct.name}
                        width={200}
                        height={200}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparisonFields.map((field) => (
              <tr key={field.type}>
                <th scope="row" className={styles.fieldLabel}>
                  {field.label}
                </th>
                {validProducts.map((validProduct, productIndex) => {
                  const fieldContent = renderComparisonField(
                    validProduct,
                    field.specKey
                  );
                  const isNameField =
                    field.specKey === PRODUCT_NAME_EN ||
                    field.specKey === PRODUCT_NAME_FR;

                  return (
                    <td
                      key={`${validProduct.code}-${field.type}`}
                      className={`${styles.comparisonItemField} ${
                        productIndex === 0 ? styles.alternateColumn : ''
                      }`}
                      tabIndex={isNameField ? -1 : 0}
                      role="gridcell"
                    >
                      {fieldContent || <p>{t('not.applicable')}</p>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ProductComparison;
